import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

/**
 * Catches render-time failures (a lazy page chunk that never arrived, or a
 * runtime error) and shows a French recovery screen instead of a blank page.
 * The technical detail is shown in small print so a screenshot tells us the
 * real cause.
 */
class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, errorMessage: "" };

  static getDerivedStateFromError(error: unknown): State {
    const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    return { hasError: true, errorMessage: msg.slice(0, 300) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("App error boundary caught:", error, info);
  }

  handleReload = () => {
    // Clear the one-shot chunk-reload guard so the retry logic can run again.
    try {
      sessionStorage.removeItem("lcs_chunk_reload");
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = /dynamically imported module|Loading chunk|Importing a module script failed|Failed to fetch/i.test(
        this.state.errorMessage
      );
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Une erreur est survenue
          </h1>
          <p className="text-muted-foreground max-w-md mb-8">
            {isChunkError
              ? "La page n'a pas pu se charger complètement. Cela arrive parfois quand la connexion est instable. Rechargez la page pour réessayer."
              : "Un problème technique a interrompu la page. Rechargez la page pour réessayer. Si le problème revient, envoyez une capture de cet écran à nahema@quirkyfrench.com."}
          </p>
          <Button onClick={this.handleReload} size="lg" className="gap-2">
            <RotateCcw className="h-5 w-5" />
            Recharger la page
          </Button>
          {this.state.errorMessage && (
            <p className="mt-8 max-w-md text-xs text-muted-foreground/70 break-words" translate="no">
              Détail technique : {this.state.errorMessage}
            </p>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
